import { useMemo } from 'react';
import type { ToneOfVoice } from '@/types/scheduling';

interface DescriptionPreviewProps {
  readonly toneOfVoice: ToneOfVoice;
  readonly hasRecurringCta: boolean;
  readonly recurringCtaText: string;
  readonly hasEmojis: boolean;
}

// Sample description - shortened version
const DESCRIPTION_PARTS = {
  intro: {
    vous: "Vrai ou Faux ?",
    tu: "Vrai ou Faux ?",
  },
  hook: {
    vous: "Sur les réseaux, vous devez être visible pour être reconnu.",
    tu: "Sur les réseaux, tu dois être visible pour être reconnu.",
  },
  problem: {
    vous: "Problème ? Les experts ont le « savoir-faire » mais pas le « faire-savoir »",
    tu: "Problème ? Les experts ont le « savoir-faire » mais pas le « faire-savoir »",
  },
  consequence: {
    vous: "Résultat : le marché abonde de « faux experts ».",
    tu: "Résultat : le marché abonde de « faux experts ».",
  },
  mission: {
    vous: "Remettre les vrais experts au coeur de la création de contenu, c'est notre mission chez @agencepersonnelle",
    tu: "Remettre les vrais experts au coeur de la création de contenu, c'est notre mission chez @agencepersonnelle",
  },
  cta: {
    vous: "Vous connaissez un expert qui mérite plus de visibilité ?\nTaguez-le en commentaires.",
    tu: "Tu connais un expert qui mérite plus de visibilité ?\nTague-le en commentaires.",
  },
};

const EMOJIS = {
  intro: "🤷",
  problem: "📣",
  mission: "❤️",
};

export function DescriptionPreview({ 
  toneOfVoice, 
  hasRecurringCta, 
  recurringCtaText,
  hasEmojis 
}: DescriptionPreviewProps) {
  const description = useMemo(() => {
    const tone = toneOfVoice || 'vous';
    const emoji = hasEmojis ? EMOJIS : { intro: '', problem: '', mission: '' };
    
    return {
      intro: `${DESCRIPTION_PARTS.intro[tone]} ${emoji.intro}`.trim(),
      hook: DESCRIPTION_PARTS.hook[tone],
      problem: `${DESCRIPTION_PARTS.problem[tone]} ${emoji.problem}`.trim(),
      consequence: DESCRIPTION_PARTS.consequence[tone],
      mission: hasEmojis 
        ? DESCRIPTION_PARTS.mission[tone].replace('au coeur', `au coeur ${emoji.mission}`)
        : DESCRIPTION_PARTS.mission[tone],
      cta: DESCRIPTION_PARTS.cta[tone],
    };
  }, [toneOfVoice, hasEmojis]);

  return (
    <div className="w-full h-full bg-black flex items-center justify-center p-6 overflow-hidden">
      <div className="max-w-md w-full max-h-full overflow-y-auto scrollbar-hide">
        {/* Instagram-style description */}
        <div className="space-y-4 text-white text-sm leading-relaxed">
          {/* Username and intro */}
          <p>
            <span className="font-semibold">Prsnl</span>{' '}
            <span>{description.intro}</span>
          </p>

          {/* Hook */}
          <p>{description.hook}</p>

          {/* Problem */}
          <p>{description.problem}</p>

          {/* Consequence */}
          <p>{description.consequence}</p>

          {/* Mission with mention */}
          <p>
            {description.mission.split('@agencepersonnelle')[0]}
            <span className="text-primary">@agencepersonnelle</span>
          </p>

          {/* CTA - changes based on tone */}
          <p className="whitespace-pre-line">{description.cta}</p>

          {/* Recurring CTA if enabled */}
          {hasRecurringCta && recurringCtaText && (
            <p className="pt-2 border-t border-white/10 text-warning/90 animate-in fade-in slide-in-from-bottom-2 duration-300">
              {recurringCtaText}
            </p>
          )}

        </div>
      </div>
    </div>
  );
}
